import { QueryClientProvider, useQuery } from '@tanstack/react-query';
import { applyBrandingColors } from '@/lib/utils';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useRef, useCallback, type CSSProperties } from 'react';
import { queryClient } from '@/lib/query-client';
import { apiClient } from '@/lib/api-client';
import { useAuth, useAuthStore, setupSessionExpiryListener } from '@/features/auth/hooks/useAuth';
import {
  LoginPage,
  ProtectedRoute,
  ModuleGuard,
  AcceptInvitation,
  PasswordReset,
} from '@/features/auth';
import { AppLayout, Sidebar, type SidebarItem } from '@/components/layout';
import { isSidebarItemVisible } from '@/components/layout/sidebarAccess';
import { usePermissions, PERMISSIONS } from '@/hooks/usePermissions';
import { ToastProvider } from '@/components/ui/Toast';
import { ApiToastInterceptor } from '@/lib/api-toast-interceptor';
import { DashboardPage } from '@/features/dashboard';
import {
  ContactsListPage,
  ContactDetailPage,
  ContactForm,
  NotificationSettingsPage,
} from '@/features/contacts';
import { ContactGroupsPage } from '@/features/contact-groups';
import { JobsListPage, JobDetailPage, JobForm, KanbanBoard } from '@/features/jobs';
import {
  BillingTransactionsListPage,
  InvoiceForm,
  BillingTransactionForm,
  ClientLedgerPage,
  ClientLedgerSelectPage,
  FinancialPeriodsPage,
} from '@/features/billing';
import { DocumentsPage } from '@/features/documents';
import { MessagesPage } from '@/features/messages';
import { NotificationsInboxPage } from '@/features/notifications';
import { TimesheetPage, TimesheetApprovalPage } from '@/features/time-tracking';
import { ReportsPage } from '@/features/reports';
import { SettingsPage } from '@/features/settings';
import {
  FormBuilderPage,
  FormEditorPage,
  FormMappingsPage,
  FieldLibraryPage,
} from '@/features/form-builder';
import {
  DocumentTemplatesPage,
  TemplateEditorPage,
  GeneratedDocumentsPage,
} from '@/features/document-templates';
import { AutomationRuleDesigner } from '@/features/automation';
import { CapacityDashboard } from '@/features/capacity';
import { ESignaturePage } from '@/features/e-signature';
import { EmailSyncPage } from '@/features/email-sync';
import { MigrationWizard } from '@/features/migration';
import { PracticeEntitiesPage } from '@/features/practice-entities';
import { SlaDashboard } from '@/features/sla';
import { WipLedgerPage } from '@/features/wip';

const NAV_ITEMS: SidebarItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    href: '/dashboard',
    permission: 'dashboard.view',
    module: 'dashboard',
    icon: (
      <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M2.25 12l8.954-8.955a1.126 1.126 0 011.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"
        />
      </svg>
    ),
  },
  {
    id: 'contacts',
    label: 'Contacts',
    href: '/contacts',
    permission: PERMISSIONS.CONTACTS_READ,
    module: 'contacts',
    icon: (
      <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
        />
      </svg>
    ),
  },
  {
    id: 'contact-groups',
    label: 'Groups',
    href: '/contact-groups',
    permission: PERMISSIONS.GROUPS_READ,
    module: 'contacts',
    icon: (
      <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z"
        />
      </svg>
    ),
  },
  {
    id: 'jobs',
    label: 'Jobs',
    href: '/jobs',
    permission: PERMISSIONS.JOBS_READ,
    module: 'jobs',
    icon: (
      <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0M12 12.75h.008v.008H12v-.008z"
        />
      </svg>
    ),
  },
  {
    id: 'time-tracking',
    label: 'Time Tracking',
    href: '/time-tracking',
    permission: 'time_entries.read',
    module: 'time-entries',
    icon: (
      <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  },
  {
    id: 'capacity',
    label: 'Capacity',
    href: '/capacity',
    // Gate on the dedicated capacity read permission.
    permission: PERMISSIONS.CAPACITY_READ,
    module: 'capacity',
    icon: (
      <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5"
        />
      </svg>
    ),
  },
  {
    id: 'billing',
    label: 'Billing',
    href: '/billing',
    permission: PERMISSIONS.BILLING_READ,
    module: 'billing',
    icon: (
      <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18v-.008zm-12 0h.008v.008H6v-.008z"
        />
      </svg>
    ),
  },
  {
    id: 'client-ledger',
    label: 'Client Ledger',
    href: '/client-ledger',
    permission: PERMISSIONS.BILLING_VIEW_LEDGER,
    module: 'client-ledger',
    icon: (
      <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
        />
      </svg>
    ),
  },
  {
    id: 'wip',
    label: 'WIP Ledger',
    href: '/wip',
    // Gate on WIP read, not billing read, so users without WIP access don't
    // see the WIP Ledger item.
    permission: PERMISSIONS.WIP_READ,
    module: 'wip',
    icon: (
      <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  },
  {
    id: 'financial-periods',
    label: 'Financial Periods',
    href: '/settings/financial-periods',
    permission: PERMISSIONS.BILLING_MANAGE_PERIODS,
    icon: (
      <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
        />
      </svg>
    ),
  },
  {
    id: 'documents',
    label: 'Documents',
    href: '/documents',
    permission: PERMISSIONS.DOCUMENTS_READ,
    module: 'documents',
    icon: (
      <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
        />
      </svg>
    ),
  },
  {
    id: 'messages',
    label: 'Messages',
    href: '/messages',
    permission: 'messages.read',
    module: 'messages',
    icon: (
      <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
        />
      </svg>
    ),
  },
  {
    id: 'notifications',
    label: 'Notifications',
    href: '/notifications',
    permission: PERMISSIONS.NOTIFICATIONS_READ,
    module: 'notifications',
    icon: (
      <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
        />
      </svg>
    ),
  },
  {
    id: 'notification-settings',
    label: 'Notification Settings',
    href: '/notification-settings',
    permission: PERMISSIONS.CONTACTS_MANAGE_NOTIFICATIONS,
    icon: (
      <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9.143 17.082a24.248 24.248 0 003.844.148m-3.844-.148a23.856 23.856 0 01-5.455-1.31 8.964 8.964 0 002.3-5.542m3.155 6.852a3 3 0 005.667 1.97m1.965-6.372a8.966 8.966 0 01.985-4.32m0 0a8.997 8.997 0 00-6.75-3.056M4.5 8.25a8.966 8.966 0 016.75-3.056m0 0V3m0 2.194a8.997 8.997 0 016.75 3.056"
        />
      </svg>
    ),
  },
  {
    id: 'reports',
    label: 'Reports',
    href: '/reports',
    permission: PERMISSIONS.REPORTING_READ,
    module: 'reports',
    icon: (
      <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"
        />
      </svg>
    ),
  },
  {
    id: 'automation',
    label: 'Automation',
    href: '/automation',
    permission: 'workflow_management.read',
    module: 'automation',
    icon: (
      <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z"
        />
      </svg>
    ),
  },
  {
    id: 'migration',
    label: 'Data Migration',
    href: '/migration',
    permission: PERMISSIONS.MIGRATION_READ,
    module: 'migration',
    icon: (
      <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M7.5 7.5h-.75A2.25 2.25 0 004.5 9.75v7.5a2.25 2.25 0 002.25 2.25h7.5a2.25 2.25 0 002.25-2.25v-7.5a2.25 2.25 0 00-2.25-2.25h-.75m-6 3.75l3 3m0 0l3-3m-3 3V1.5"
        />
      </svg>
    ),
  },
  {
    id: 'settings',
    label: 'Settings',
    href: '/settings',
    permission: PERMISSIONS.SETTINGS_VIEW,
    icon: (
      <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z"
        />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
];

function AppSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { can, canAny, isAdmin } = usePermissions();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const enabledModules = useAuthStore((s) => s.enabledModules);

  // Fetch unread notification count for badge
  const { data: unreadCount } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: { unread_count: number } }>(
        '/notifications/unread-count',
      );
      const count = response.data.data?.unread_count ?? 0;
      // Sync the notification store so sidebar and notification center show the same count
      const { useNotificationStore } =
        await import('@/features/notifications/hooks/useNotifications');
      useNotificationStore.getState().setUnreadCount(count);
      return count;
    },
    // Only poll while authenticated — prevents 401s when the token is
    // missing/expired (e.g. right after logout or session expiry).
    enabled: isAuthenticated,
    refetchInterval: isAuthenticated ? 30_000 : false, // Refresh every 30 seconds
  });

  // Unread client-message count for the Messages sidebar badge.
  const { data: messagesUnread } = useQuery({
    queryKey: ['messages', 'unread-count'],
    queryFn: async () => {
      const response = await apiClient.get<{
        success: boolean;
        data: { unread_count: number };
      }>('/messages/threads/unread-count');
      return response.data.data?.unread_count ?? 0;
    },
    enabled: isAuthenticated,
    refetchInterval: isAuthenticated ? 30_000 : false,
    refetchIntervalInBackground: false,
  });

  // Firm branding (Settings → Branding) for the sidebar logo, name, and tint.
  const { data: branding } = useQuery({
    queryKey: ['settings', 'branding', 'sidebar'],
    queryFn: async () => {
      const res = await apiClient.get<{
        success: boolean;
        data: {
          firmName: string | null;
          logoUrl: string | null;
          primaryColor: string | null;
          secondaryColor: string | null;
        };
      }>('/settings/branding');
      return res.data.data;
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    applyBrandingColors(branding?.primaryColor ?? null, branding?.secondaryColor ?? null);
  }, [branding?.primaryColor, branding?.secondaryColor]);

  const brandGradient = (alpha: number, angle = 135): CSSProperties | undefined => {
    const from = branding?.primaryColor;
    const to = branding?.secondaryColor || branding?.primaryColor;
    if (!from) return undefined;
    const rgba = (hex: string, a: number): string => {
      const c = hex.replace('#', '');
      if (c.length !== 6) return hex;
      const n = parseInt(c, 16);
      if (Number.isNaN(n)) return hex;
      return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
    };
    return {
      backgroundImage: `linear-gradient(${angle}deg, ${rgba(from, alpha)} 0%, ${rgba(to as string, alpha)} 100%)`,
    };
  };
  // Sidebar body carries a lighter tint; its header mirrors the main header's
  // gradient horizontally (225deg vs 135deg). Its opacity is intentionally
  // lower (0.08) than the main header's 0.16 because it stacks on top of the
  // 0.10 body tint — the combined layers read at a similar strength to the
  // main header's single 0.16 wash.
  const sidebarStyle = brandGradient(0.1);
  const sidebarHeaderStyle = brandGradient(0.08, 225);

  const activeItemId = NAV_ITEMS.find((item) => {
    if (!item.href) return false;
    if (item.href === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(item.href);
  })?.id;

  const handleLogout = useCallback(() => {
    logout.mutate(undefined, {
      onSettled: () => {
        navigate('/auth/login');
      },
    });
  }, [logout, navigate]);

  const handleItemClick = useCallback(
    (item: SidebarItem) => {
      if (item.id === 'logout') {
        handleLogout();
        return;
      }
      if (item.href) {
        navigate(item.href);
      }
    },
    [navigate, handleLogout],
  );

  const navItems: SidebarItem[] = [
    ...NAV_ITEMS.filter((item) =>
      isSidebarItemVisible(item, isAdmin, can, canAny, enabledModules),
    ).map((item) => {
      if (item.id === 'notifications' && unreadCount && unreadCount > 0) {
        return { ...item, badge: unreadCount };
      }
      if (item.id === 'messages' && messagesUnread && messagesUnread > 0) {
        return { ...item, badge: messagesUnread };
      }
      return item;
    }),
    {
      id: 'logout',
      label: 'Sign Out',
      icon: (
        <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9"
          />
        </svg>
      ),
    },
  ];

  return (
    <Sidebar
      items={navItems}
      activeItemId={activeItemId}
      onItemClick={handleItemClick}
      style={sidebarStyle}
      headerStyle={sidebarHeaderStyle}
      header={
        <div className="flex min-w-0 items-center gap-2">
          {branding?.logoUrl ? (
            <img
              src={branding.logoUrl}
              alt={`${branding.firmName || 'Firm'} logo`}
              className="h-8 w-8 shrink-0 rounded object-contain"
            />
          ) : null}
          <div className="min-w-0">
            <div className="truncate text-sm font-bold text-gray-900 dark:text-gray-100">
              {branding?.firmName || 'Back Office'}
            </div>
            <div className="truncate text-xs font-medium text-gray-500 dark:text-gray-400">
              Back Office
            </div>
          </div>
        </div>
      }
    />
  );
}

function DefaultRedirect() {
  const { can, canAny, isAdmin } = usePermissions();
  const enabledModules = useAuthStore((s) => s.enabledModules);
  const navigate = useNavigate();

  useEffect(() => {
    const firstPermitted = NAV_ITEMS.find(
      (item) => item.href && isSidebarItemVisible(item, isAdmin, can, canAny, enabledModules),
    );
    navigate(firstPermitted?.href ?? '/dashboard', { replace: true });
  }, [can, canAny, isAdmin, enabledModules, navigate]);

  return null;
}

function AuthenticatedApp() {
  return (
    <>
      <AppLayout sidebar={<AppSidebar />}>
        <Routes>
          <Route path="/" element={<DefaultRedirect />} />
          <Route
            path="/dashboard"
            element={
              <ModuleGuard module="dashboard">
                <DashboardPage />
              </ModuleGuard>
            }
          />
          <Route
            path="/contacts"
            element={
              <ModuleGuard module="contacts">
                <ContactsListPage />
              </ModuleGuard>
            }
          />
          <Route
            path="/contacts/new"
            element={
              <ModuleGuard module="contacts">
                <ContactForm />
              </ModuleGuard>
            }
          />
          <Route
            path="/contacts/:contactId"
            element={
              <ModuleGuard module="contacts">
                <ContactDetailPage />
              </ModuleGuard>
            }
          />
          <Route
            path="/contacts/:contactId/edit"
            element={
              <ModuleGuard module="contacts">
                <ContactForm />
              </ModuleGuard>
            }
          />
          <Route
            path="/contact-groups"
            element={
              <ModuleGuard module="contacts">
                <ContactGroupsPage />
              </ModuleGuard>
            }
          />
          <Route path="/notification-settings" element={<NotificationSettingsPage />} />
          <Route
            path="/jobs"
            element={
              <ModuleGuard module="jobs">
                <JobsListPage />
              </ModuleGuard>
            }
          />
          <Route
            path="/jobs/new"
            element={
              <ModuleGuard module="jobs">
                <JobForm />
              </ModuleGuard>
            }
          />
          <Route
            path="/jobs/kanban"
            element={
              <ModuleGuard module="jobs">
                <KanbanBoard />
              </ModuleGuard>
            }
          />
          <Route
            path="/jobs/:jobId"
            element={
              <ModuleGuard module="jobs">
                <JobDetailPage />
              </ModuleGuard>
            }
          />
          <Route
            path="/jobs/:jobId/edit"
            element={
              <ModuleGuard module="jobs">
                <JobForm />
              </ModuleGuard>
            }
          />
          <Route
            path="/billing"
            element={
              <ModuleGuard module="billing">
                <BillingTransactionsListPage />
              </ModuleGuard>
            }
          />
          <Route
            path="/billing/invoices"
            element={
              <ModuleGuard module="billing">
                <BillingTransactionsListPage />
              </ModuleGuard>
            }
          />
          {/* Unified billing transaction form — ?type=invoice|quote|credit_note|debit_note|write_off|discount */}
          <Route
            path="/billing/new"
            element={
              <ModuleGuard module="billing">
                <BillingTransactionForm />
              </ModuleGuard>
            }
          />
          <Route
            path="/billing/:invoiceId/edit"
            element={
              <ModuleGuard module="billing">
                <BillingTransactionForm />
              </ModuleGuard>
            }
          />
          <Route
            path="/billing/:invoiceId"
            element={
              <ModuleGuard module="billing">
                <BillingTransactionForm />
              </ModuleGuard>
            }
          />
          <Route
            path="/billing/invoices/new"
            element={
              <ModuleGuard module="billing">
                <InvoiceForm />
              </ModuleGuard>
            }
          />
          <Route
            path="/billing/invoices/:id/edit"
            element={
              <ModuleGuard module="billing">
                <InvoiceForm />
              </ModuleGuard>
            }
          />
          <Route
            path="/billing/clients/:clientId/ledger"
            element={
              <ModuleGuard module="client-ledger">
                <ClientLedgerPage />
              </ModuleGuard>
            }
          />
          <Route
            path="/client-ledger"
            element={
              <ModuleGuard module="client-ledger">
                <ClientLedgerSelectPage />
              </ModuleGuard>
            }
          />
          <Route
            path="/client-ledger/:clientId"
            element={
              <ModuleGuard module="client-ledger">
                <ClientLedgerPage />
              </ModuleGuard>
            }
          />
          <Route
            path="/documents/*"
            element={
              <ModuleGuard module="documents">
                <DocumentsPage />
              </ModuleGuard>
            }
          />
          <Route
            path="/time-tracking"
            element={
              <ModuleGuard module="time-entries">
                <TimesheetPage />
              </ModuleGuard>
            }
          />
          <Route
            path="/time-tracking/approvals"
            element={
              <ModuleGuard module="time-entries">
                <TimesheetApprovalPage />
              </ModuleGuard>
            }
          />
          <Route
            path="/messages/*"
            element={
              <ModuleGuard module="messages">
                <MessagesPage />
              </ModuleGuard>
            }
          />
          <Route
            path="/notifications"
            element={
              <ModuleGuard module="notifications">
                <NotificationsInboxPage />
              </ModuleGuard>
            }
          />
          <Route
            path="/reports"
            element={
              <ModuleGuard module="reports">
                <ProtectedRoute permissions={[PERMISSIONS.REPORTING_READ]}>
                  <ReportsPage />
                </ProtectedRoute>
              </ModuleGuard>
            }
          />
          <Route
            path="/automation"
            element={
              <ModuleGuard module="automation">
                <AutomationRuleDesigner />
              </ModuleGuard>
            }
          />
          <Route
            path="/capacity"
            element={
              <ModuleGuard module="capacity">
                <CapacityDashboard />
              </ModuleGuard>
            }
          />
          <Route path="/e-signatures" element={<ESignaturePage />} />
          <Route path="/email-sync" element={<EmailSyncPage />} />
          <Route
            path="/migration"
            element={
              <ModuleGuard module="migration">
                <ProtectedRoute permissions={[PERMISSIONS.MIGRATION_READ]}>
                  <MigrationWizard />
                </ProtectedRoute>
              </ModuleGuard>
            }
          />
          <Route path="/practice-entities" element={<PracticeEntitiesPage />} />
          <Route path="/sla" element={<SlaDashboard />} />
          <Route
            path="/wip"
            element={
              <ModuleGuard module="wip">
                <WipLedgerPage />
              </ModuleGuard>
            }
          />
          <Route path="/settings/form-builder" element={<FormBuilderPage />} />
          <Route path="/settings/form-builder/mappings" element={<FormMappingsPage />} />
          <Route path="/settings/form-builder/field-library" element={<FieldLibraryPage />} />
          <Route path="/settings/form-builder/:formId" element={<FormEditorPage />} />
          <Route
            path="/settings/document-templates"
            element={
              <ModuleGuard module="document-templates">
                <DocumentTemplatesPage />
              </ModuleGuard>
            }
          />
          <Route
            path="/settings/document-templates/generated"
            element={
              <ModuleGuard module="document-templates">
                <GeneratedDocumentsPage />
              </ModuleGuard>
            }
          />
          <Route
            path="/settings/document-templates/:id"
            element={
              <ModuleGuard module="document-templates">
                <TemplateEditorPage />
              </ModuleGuard>
            }
          />
          <Route path="/settings/financial-periods" element={<FinancialPeriodsPage />} />
          <Route path="/settings/*" element={<SettingsPage />} />
          <Route path="*" element={<DefaultRedirect />} />
        </Routes>
      </AppLayout>
      {/* <TimerWidget /> */}
    </>
  );
}

function AppRoutes() {
  const { initialize } = useAuth();
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const initCalled = useRef(false);

  useEffect(() => {
    if (!initCalled.current) {
      initCalled.current = true;
      initialize();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isInitialized) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface dark:bg-surface-dark">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/auth/login" element={<LoginPage />} />
      <Route path="/auth/password-reset" element={<PasswordReset />} />
      <Route path="/accept-invitation" element={<AcceptInvitation />} />
      <Route
        path="*"
        element={
          <ProtectedRoute>
            <AuthenticatedApp />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export function App() {
  useEffect(() => {
    return setupSessionExpiryListener();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <ApiToastInterceptor />
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AppRoutes />
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  );
}
